import { useState, useEffect } from 'react';

interface FooterProps {
  setCurrentPage: (
    page:
      | 'home'
      | 'about'
      | 'contact'
      | 'consultants'
      | 'companies'
      | 'popi-policy'
      | 'privacy-policy'
      | 'terms-of-service'
      | 'terms-consultants'
      | 'terms-companies'
      | 'community-guidelines'
      | 'medical-disclaimer'
      | 'ethics-committees'
      | 'rfp-submission'
      | 'analytics'
      | 'subscriptions'
      | 'login'
      | 'register'
      | 'profile'
      | 'marketplace'
      | 'community'
      | 'sites'
      | 'post-job'
  ) => void;
  isLoggedIn?: boolean;
  userRole?: string;
}

export default function Footer({ setCurrentPage, isLoggedIn, userRole }: FooterProps) {
  const [footerText, setFooterText] = useState({
    copyright:
      '© 2026 Clinical Trial Expertise, property of © 2025 Xana (PTY) Ltd. All rights reserved.',
    tagline: 'Expert Clinical Trial Consulting Services',
  });

  // Handler to navigate - scrolling is handled by App.tsx
  const handleNavClick = (page: Parameters<typeof setCurrentPage>[0]) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    fetch('/api/content')
      .then((res) => res.json())
      .then((data) => {
        setFooterText({
          copyright:
            data.footerCopyright ||
            '© 2026 Clinical Trial Expertise, property of © 2025 Xana (PTY) Ltd. All rights reserved.',
          tagline: data.footerTagline || 'Expert Clinical Trial Consulting Services',
        });
      })
      .catch(() => {
        // Use default on error
      });
  }, []);

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Clinical Trial Expertise</h3>
            <p>
              Professional consulting services for clinical research, trial management, and
              regulatory compliance.
            </p>
          </div>
          <div className="footer-section">
            <h3>Quick Links</h3>
            <ul>
              <li>
                <button onClick={() => handleNavClick('home')}>Home</button>
              </li>
              <li>
                <button onClick={() => handleNavClick('about')}>About</button>
              </li>
              {isLoggedIn && (
                <li>
                  <button onClick={() => handleNavClick('consultants')}>Consultants</button>
                </li>
              )}
              <li>
                <button onClick={() => handleNavClick('contact')}>Contact</button>
              </li>
              {isLoggedIn && (
                <li>
                  <button onClick={() => handleNavClick('marketplace')}>Jobs</button>
                </li>
              )}
              <li>
                <button onClick={() => handleNavClick('ethics-committees')}>
                  Ethics Committees
                </button>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Legal & Policies</h3>
            <ul>
              <li>
                <button onClick={() => handleNavClick('terms-of-service')}>Terms of Service</button>
              </li>
              <li>
                <button onClick={() => handleNavClick('privacy-policy')}>Privacy Policy</button>
              </li>
              <li>
                <button onClick={() => handleNavClick('popi-policy')}>POPI Policy</button>
              </li>
              {isLoggedIn && userRole === 'professional' && (
                <li>
                  <button onClick={() => handleNavClick('terms-consultants')}>
                    Terms - Consultants
                  </button>
                </li>
              )}
              {isLoggedIn && userRole === 'company' && (
                <li>
                  <button onClick={() => handleNavClick('terms-companies')}>
                    Terms - Companies
                  </button>
                </li>
              )}
              {isLoggedIn && (
                <>
                  <li>
                    <button onClick={() => handleNavClick('community-guidelines')}>
                      Community Guidelines
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleNavClick('medical-disclaimer')}>
                      Medical Disclaimer
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
          <div className="footer-section">
            <h3>Services</h3>
            <ul>
              <li>Trial Design & Protocol Development</li>
              <li>Regulatory & Compliance Consultation</li>
              <li>SOP Writing & Proofreading</li>
              <li>Quality Assurance</li>
              <li>Site Staff Training</li>
              <li>Medical Writing & Information Services</li>
              <li>Clinical Trial Monitoring</li>
              <li>Project Management</li>
              <li>Quality Oversight</li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Professional Links</h3>
            <ul>
              <li>
                <a href="https://pactr.samrc.ac.za/" target="_blank" rel="noopener noreferrer">
                  PACTR - Pan African Clinical Trials Registry
                </a>
              </li>
              <li>
                <a href="https://www.sahpra.org.za/" target="_blank" rel="noopener noreferrer">
                  SAHPRA
                </a>
              </li>
              <li>
                <a href="https://sanctr.samrc.ac.za/" target="_blank" rel="noopener noreferrer">
                  SANCTR - SA Clinical Trials Registry
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{footerText.copyright}</p>
          <p>{footerText.tagline}</p>
        </div>
      </div>
    </footer>
  );
}

